import 'moment/locale/nb';
import { differenceInMinutes, formatDistance, formatRelative, isSameYear, parseISO, } from 'date-fns';
import enLocale from 'date-fns/locale/en-US';
import noLocale from 'date-fns/locale/nb';
import { format, formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import moment from 'moment';
/**
 * Format a date to a string in the format "dd.mm.yyyy"
 */
export function formatDate(date) {
    return format(date, 'dd.MM.yyyy');
}
export function formatReadableDateTime(dateobj) {
    return format(dateobj, "d. MMMM 'kl.' HH:mm", { locale: noLocale });
}
/**
 *
 * @param personalDetails Personal details object from the usePersonalData-store
 * @returns A string with the date and time formatted according to ISO 8601
 * @example formatStartTime({ date: '2021-01-01', time: '12:00' }) // 2021-01-01T11:00:00.000Z
 */
export const formatStartTime = (personalDetails) => new Date(getTimeStamp(personalDetails.date, personalDetails.time)).toISOString();
export function getDaysUntil(date) {
    // Parse the startTime into a Moment object, and ignore the specific time of day.
    const startTime = moment(date).startOf('day');
    // Get the current date and time as a Moment object
    const today = moment().startOf('day');
    // Calculate the difference in days
    return startTime.diff(today, 'days');
}
/**
 *  Function that takes in a date in format "yyyy-MM-dd" and returns a date object based on date.
 *  This date is mostly used to determine if a given day is within a specific range i.e. sameDay/after/addDays/sameMonth,
 *  so the time stamp is set to 12:00:00 to avoid any timezone issues. This will break if the user is in a timezone that differs
 *  more than 12 hours from UTC. But this is 5 small island countries in the world, so we should be fine.
 *
 * @param date - Parse a given ISO date into a date object, mostly used to determine if a date is in a given range.
 * @returns New date object, either 12:00 on a day, or the current date.
 */
export const parseDay = (date) => {
    const isoDate = `${date}T12:00:00.000Z`;
    if (isIsoDate(isoDate)) {
        return parseISO(isoDate);
    }
    return new Date();
};
/**
 * @param date - Date object to format into the projects timezone and preferred format. Defaults to Europe/Oslo and format "yyyy-MM-dd"
 */
export const getDateInTimezone = (date = new Date()) => formatInTimeZone(date, timeZone, 'yyyy-MM-dd');
export const getFormattedWaitTime = (date = new Date(), locale = noLocale) => {
    const now = new Date();
    const duration = differenceInMinutes(date, now);
    const coff = 1000 * 60 * 5;
    const roundedNow = new Date(Math.floor(now.getTime() / coff) * coff);
    const durationNearest10 = Math.ceil(duration / 10) * 10;
    return {
        displayName: durationNearest10 <= 5
            ? 'ca. 5 min'
            : formatDistance(date, roundedNow, {
                locale: { ...locale, formatDistance: formatDistanceCustom },
            }),
        // Wack hack to add 10 minutes to the duration, to make sure the time is always rounded up and added extra time.
        // Should handle this from the backend
        duration: durationNearest10 + 10,
    };
};
export const norwayTimeZone = 'Europe/Oslo';
export const appLocales = {
    nbApp: {
        ...noLocale,
        formatRelative: (token) => ({
            lastWeek: "'Forrige' eeee 'kl' p",
            yesterday: "'I går kl.' p",
            today: "'ca.' HH:mm",
            tomorrow: "'I morgen kl.' HH.mm",
            nextWeek: "dd. MMM 'kl.' HH.mm",
            other: "dd. MMMM 'kl.' HH.mm",
        })[token],
    },
};
/* Override format relative of default locale to truly make it ours */
export const locales = {
    nb: {
        ...noLocale,
        formatRelative: (token) => ({
            lastWeek: "'Forrige' eeee 'kl' p",
            yesterday: "'I går kl.' p",
            today: "'I dag kl.' HH.mm",
            tomorrow: "'I morgen kl.' HH.mm",
            nextWeek: "dd. MMM 'kl.' HH.mm",
            other: "dd. MMMM 'kl.' HH.mm",
        })[token],
    },
    en: {
        ...enLocale,
        formatRelative: (token) => ({
            lastWeek: "'Last' eeee 'at' HH:mm",
            yesterday: "'Yesterday at' HH:mm",
            today: "'Today at' HH:mm",
            tomorrow: "'Tomorrow at' HH:mm",
            nextWeek: "dd. MMM 'at' HH:mm",
            other: "dd. MMMM 'at' HH:mm",
        })[token],
    },
};
export const localesNextAvailableWithTime = {
    nb: {
        ...noLocale,
        formatRelative: (token) => ({
            lastWeek: "'Forrige' eeee 'kl' p",
            yesterday: "'I går kl.' p",
            today: "'Ledig i dag'",
            tomorrow: "'Ledig i morgen'",
            nextWeek: "'Ledig' d. MMMM",
            other: "dd. MMMM 'kl.' HH.mm",
        })[token],
    },
    en: {
        ...enLocale,
        formatRelative: (token) => ({
            lastWeek: "'Last' eeee 'at' HH:mm",
            yesterday: "'Yesterday at' HH:mm",
            today: "'Available today'",
            tomorrow: "'Available tomorrow'",
            nextWeek: "'Available' d. MMMM",
            other: "dd. MMMM 'at' HH:mm",
        })[token],
    },
};
/* Override format relative of default locale to truly make it ours, also when excluding the exact time. */
export const localesDateOnly = {
    nb: {
        ...noLocale,
        formatRelative: (token) => ({
            lastWeek: "'Forrige uke'",
            yesterday: "'I går'",
            today: "'I dag'",
            tomorrow: "'I morgen'",
            nextWeek: 'dd. MMM',
            other: 'dd. MMMM',
        })[token],
    },
    en: {
        ...enLocale,
        formatRelative: (token) => ({
            lastWeek: "'Last week'",
            yesterday: "'Yesterday",
            today: "'Today",
            tomorrow: "'Tomorrow",
            nextWeek: 'dd. MMM',
            other: 'dd. MMMM',
        })[token],
    },
};
const formatDistanceLocale = {
    lessThanXSeconds: 'ca. {{count}}s',
    xSeconds: 'ca. {{count}}s',
    halfAMinute: 'ca. 30s',
    lessThanXMinutes: 'ca. {{count}} min',
    xMinutes: 'ca. {{count}} min',
    aboutXHours: 'ca. {{count}} timer',
    xHours: 'ca. {{count}} timer',
    xDays: 'ca. {{count}} dager',
    aboutXWeeks: 'ca. {{count}} uker',
    xWeeks: '{{count}} uker',
    aboutXMonths: 'omtrent {{count}} måneder',
    xMonths: '{{count}} måneder',
    aboutXYears: 'omtrent {{count}} år',
    xYears: '{{count}} år',
    overXYears: 'over {{count}} år',
    almostXYears: 'nesten {{count}} år',
};
function formatDistanceCustom(token, count, options) {
    options = options || {};
    const result = formatDistanceLocale[token].replace('{{count}}', count);
    if (options.addSuffix) {
        if (options.comparison > 0) {
            return 'in ' + result;
        }
        else {
            return result + ' ago';
        }
    }
    return result;
}
export const timeZone = process.env.GATSBY_TIMEZONE || 'Europe/Oslo';
/**
 * @param selectedDate - Selected date in ISO format
 * @param selectedTime -  Selected time in ISO format
 * @returns Milliseoconds since epoch for selected appointment, deducted the timezone offset
 */
export const getTimeStamp = (selectedDate, selectedTime) => {
    const date = new Date(`${selectedDate}T${selectedTime}Z`);
    const offset = getTimezoneOffset(timeZone, date);
    return date.valueOf() - offset;
};
/**
 * @param date - Date string in ISO format
 * @param time - Time in ISO format: "yyyy-MM-ddTHH:mm:ss.SSSZ"
 */
export const getISOString = (date, time) => new Date(`${date}T${time}`).toISOString();
/**
 * @param date - Date in format "dd/mm/yyyy", from birthdate input. Example: "24/12/2022"
 * @returns Date in format dd.MM.yyyy
 */
export function getFormattedBirthDate(date) {
    const [day, month, year] = date.split(/\//).map((d) => parseInt(d));
    return format(new Date(year, month > 0 ? month - 1 : 0, day), 'dd.MM.yyyy');
}
const nextYearFormat = {
    nb: "dd. MMM yyyy, 'kl.' HH.mm",
    en: 'dd. MMM yyyy, HH:mm',
};
/**
 * @param date - Parse date to check
 * @returns true/false if the date provided is not a valid date, e.g. "202-13-4123" was attempted parsed.
 */
const isValidDate = (date) => date instanceof Date && !isNaN(date.getTime());
/**
 * Format the provided date and time to a more human-readable format.
 * Rules:
 *  - If the date is today, show "I dag" or "Today"
 *   - If the date is tomorrow, show "I morgen" or "Tomorrow"
 *   - If the date is in the next 7 days, show the weekday
 *   - If the year is later than the current year, show year
 * @param date - Date in format "yyyy-mm-dd", from selected date. Example: "2022-12-24"
 * @param time - Selected time in format "HH:mm", Example: "12:00"
 * @param locale - Users current locale, governed by i18n
 * @returns Date in format Defaults to "24. december, kl: 12:00", but depends on requestedFormat param
 */
export const getFormattedAppointmentDateTime = (date, time, locale, isAndroid = false) => {
    // Android does not convert ISO strings to local time, so we need to convert it with timestamp
    const selectedDate = isAndroid
        ? new Date(getTimeStamp(date, time))
        : new Date(getISOString(date, time));
    if (!isValidDate(selectedDate))
        return '';
    if (isSameYear(selectedDate, new Date())) {
        return formatRelative(selectedDate, new Date(), {
            locale: locales[locale],
            weekStartsOn: 1,
        });
    }
    return format(selectedDate, nextYearFormat[locale], {
        locale: locales[locale],
    });
};
/**
 * @param date - Date in string format to test for validity.
 * @returns true/false depending on the validity of the date. Example "2022-12-24:12:00:00.000Z" is valid. "2022-24-12" is not.
 */
export const isIsoDate = (date) => {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(date))
        return false;
    const d = new Date(date);
    return d instanceof Date && !isNaN(d.getTime()) && d.toISOString() === date;
};
